import { useEffect, useState } from 'react';
import {
    Typography, Button, TextField, Select, MenuItem, FormControl, InputLabel,
    Table, TableHead, TableRow, TableCell, TableBody, Card, CardContent,
    makeStyles, CircularProgress, Snackbar, IconButton, Chip,
} from '@material-ui/core';
import Alert from '@material-ui/lab/Alert';
import DeleteIcon from '@material-ui/icons/Delete';
import AddIcon from '@material-ui/icons/Add';
import { getTournaments } from '../../data';
import { getAdminPlayers, scrapeAdminPlayers, updateAdminPlayers } from '../../data/admin';

const useStyles = makeStyles((theme) => ({
    root: {
        padding: theme.spacing(2),
        maxWidth: 1200,
        margin: '0 auto',
    },
    section: {
        marginBottom: theme.spacing(3),
    },
    formRow: {
        display: 'flex',
        gap: theme.spacing(2),
        alignItems: 'center',
        marginBottom: theme.spacing(2),
        flexWrap: 'wrap',
    },
    tableContainer: {
        overflowX: 'auto',
        marginTop: theme.spacing(2),
        maxHeight: 600,
        overflow: 'auto',
    },
    diffSection: {
        marginTop: theme.spacing(2),
        marginBottom: theme.spacing(2),
    },
    teamUrlsInput: {
        width: '100%',
        fontFamily: 'monospace',
    },
}));

const POSITIONS = ['BAT', 'BOW', 'AR', 'WK'];

const DEFAULT_TEAM_URLS_PLACEHOLDER = `CSK=https://www.espncricinfo.com/series/...\nDC=https://www.espncricinfo.com/series/...\nGT=https://www.espncricinfo.com/series/...`;

export default function PlayersManager() {
    const classes = useStyles();
    const [tournaments, setTournaments] = useState([]);
    const [tournament, setTournament] = useState('');
    const [players, setPlayers] = useState([]);
    const [originalPlayers, setOriginalPlayers] = useState([]);
    const [loading, setLoading] = useState(false);
    const [scraping, setScraping] = useState(false);
    const [saving, setSaving] = useState(false);
    const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
    const [filter, setFilter] = useState('');
    const [teamUrlsText, setTeamUrlsText] = useState('');

    useEffect(() => {
        (async () => {
            const data = await getTournaments();
            setTournaments(data);
        })();
    }, []);

    const handleLoad = async () => {
        if (!tournament) return;
        setLoading(true);
        try {
            const data = await getAdminPlayers(tournament);
            const playerList = data.players || [];
            setPlayers(JSON.parse(JSON.stringify(playerList)));
            setOriginalPlayers(JSON.parse(JSON.stringify(playerList)));
        } catch (e) {
            setSnackbar({ open: true, message: e.message, severity: 'error' });
        }
        setLoading(false);
    };

    const parseTeamUrls = () => {
        const lines = teamUrlsText.split('\n').filter(l => l.trim());
        const teamUrls = {};
        for (const line of lines) {
            const eqIndex = line.indexOf('=');
            if (eqIndex === -1) continue;
            const team = line.slice(0, eqIndex).trim();
            const url = line.slice(eqIndex + 1).trim();
            if (team && url) teamUrls[team] = url;
        }
        return teamUrls;
    };

    const handleScrape = async () => {
        if (!tournament) return;
        const teamUrls = parseTeamUrls();
        if (Object.keys(teamUrls).length === 0) {
            setSnackbar({ open: true, message: 'Enter at least one TEAM=URL line', severity: 'error' });
            return;
        }
        setScraping(true);
        try {
            const result = await scrapeAdminPlayers(tournament, teamUrls);
            if (result.success) {
                setPlayers(result.data.players);
                setSnackbar({ open: true, message: `Scraped ${result.data.players.length} players`, severity: 'success' });
            } else {
                setSnackbar({ open: true, message: result.error, severity: 'error' });
            }
        } catch (e) {
            setSnackbar({ open: true, message: e.message, severity: 'error' });
        }
        setScraping(false);
    };

    const handleSave = async () => {
        setSaving(true);
        try {
            const result = await updateAdminPlayers(tournament, players);
            if (result.success) {
                setOriginalPlayers(JSON.parse(JSON.stringify(players)));
                setSnackbar({ open: true, message: 'Players saved successfully', severity: 'success' });
            } else {
                setSnackbar({ open: true, message: result.error, severity: 'error' });
            }
        } catch (e) {
            setSnackbar({ open: true, message: e.message, severity: 'error' });
        }
        setSaving(false);
    };

    const handleFieldChange = (index, field, value) => {
        const updated = [...players];
        updated[index] = { ...updated[index], [field]: value };
        setPlayers(updated);
    };

    const handleDelete = (index) => {
        setPlayers(players.filter((_, i) => i !== index));
    };

    const handleAddPlayer = () => {
        setPlayers([...players, { id: '', name: '', pos: 'BAT', team: '', overseas: false }]);
    };

    // Compute diff between original and current players
    const computeDiff = () => {
        const oldMap = {};
        const newMap = {};
        originalPlayers.forEach(p => { oldMap[p.id] = p; });
        players.forEach(p => { newMap[p.id] = p; });

        const added = players.filter(p => p.id && !oldMap[p.id]);
        const removed = originalPlayers.filter(p => p.id && !newMap[p.id]);
        const changed = players.filter(p => {
            if (!p.id || !oldMap[p.id]) return false;
            const old = oldMap[p.id];
            return old.name !== p.name || old.pos !== p.pos || old.team !== p.team;
        });

        return { added, removed, changed };
    };

    const diff = computeDiff();
    const hasDiff = diff.added.length > 0 || diff.removed.length > 0 || diff.changed.length > 0;

    const filteredPlayers = filter
        ? players.filter(p =>
            p.name.toLowerCase().includes(filter.toLowerCase()) ||
            p.team.toLowerCase().includes(filter.toLowerCase()) ||
            p.pos.toLowerCase().includes(filter.toLowerCase()))
        : players;

    return (
        <div className={classes.root}>
            <Typography variant="h5" gutterBottom>Players Manager</Typography>

            <Card className={classes.section}>
                <CardContent>
                    <div className={classes.formRow}>
                        <FormControl style={{ minWidth: 200 }}>
                            <InputLabel>Tournament</InputLabel>
                            <Select value={tournament} onChange={(e) => setTournament(e.target.value)}>
                                {tournaments.map(t => (
                                    <MenuItem key={t.key} value={t.key}>{t.label}</MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                        <Button variant="contained" color="primary" onClick={handleLoad} disabled={!tournament || loading}>
                            {loading ? <CircularProgress size={20} /> : 'Load Existing'}
                        </Button>
                    </div>
                    <Typography variant="subtitle2" gutterBottom>
                        Team URLs (one per line: TEAM=url)
                    </Typography>
                    <TextField
                        className={classes.teamUrlsInput}
                        multiline
                        rows={6}
                        variant="outlined"
                        value={teamUrlsText}
                        onChange={(e) => setTeamUrlsText(e.target.value)}
                        placeholder={DEFAULT_TEAM_URLS_PLACEHOLDER}
                        size="small"
                    />
                    <div className={classes.formRow} style={{ marginTop: 12 }}>
                        <Button variant="contained" color="secondary" onClick={handleScrape}
                            disabled={!tournament || scraping}>
                            {scraping ? <CircularProgress size={20} /> : 'Fetch Players'}
                        </Button>
                    </div>
                </CardContent>
            </Card>

            {hasDiff && (
                <Card className={classes.diffSection}>
                    <CardContent>
                        <Typography variant="h6" gutterBottom>Changes Summary</Typography>
                        {diff.added.length > 0 && (
                            <>
                                <Typography variant="subtitle2" style={{ color: '#28a745' }}>
                                    Added ({diff.added.length})
                                </Typography>
                                {diff.added.map(p => (
                                    <Chip key={p.id} label={`${p.name} (${p.team}, ${p.pos})`}
                                        size="small" style={{ margin: 2 }} />
                                ))}
                            </>
                        )}
                        {diff.removed.length > 0 && (
                            <>
                                <Typography variant="subtitle2" style={{ color: '#dc3545', marginTop: 8 }}>
                                    Removed ({diff.removed.length})
                                </Typography>
                                {diff.removed.map(p => (
                                    <Chip key={p.id} label={`${p.name} (${p.team}, ${p.pos})`}
                                        size="small" style={{ margin: 2 }} />
                                ))}
                            </>
                        )}
                        {diff.changed.length > 0 && (
                            <>
                                <Typography variant="subtitle2" style={{ color: '#ffc107', marginTop: 8 }}>
                                    Changed ({diff.changed.length})
                                </Typography>
                                {diff.changed.map(p => {
                                    const old = originalPlayers.find(o => o.id === p.id);
                                    return (
                                        <Chip key={p.id}
                                            label={`${old.name} → ${p.name} | ${old.pos} → ${p.pos} | ${old.team} → ${p.team}`}
                                            size="small" style={{ margin: 2 }} />
                                    );
                                })}
                            </>
                        )}
                    </CardContent>
                </Card>
            )}

            {players.length > 0 && (
                <Card className={classes.section}>
                    <CardContent>
                        <div className={classes.formRow}>
                            <Typography variant="h6">
                                Players ({players.length})
                            </Typography>
                            <TextField label="Filter" value={filter} size="small"
                                onChange={(e) => setFilter(e.target.value)}
                                placeholder="name, team, or pos" style={{ width: 200 }} />
                        </div>
                        <div className={classes.tableContainer}>
                            <Table size="small" stickyHeader>
                                <TableHead>
                                    <TableRow>
                                        <TableCell>ID</TableCell>
                                        <TableCell>Name</TableCell>
                                        <TableCell>Position</TableCell>
                                        <TableCell>Team</TableCell>
                                        <TableCell>Overseas</TableCell>
                                        <TableCell></TableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {filteredPlayers.map((player, i) => {
                                        const realIndex = players.indexOf(player);
                                        return (
                                            <TableRow key={i}>
                                                <TableCell>
                                                    <Typography variant="body2">{player.id}</Typography>
                                                </TableCell>
                                                <TableCell>
                                                    <TextField value={player.name} size="small"
                                                        onChange={(e) => handleFieldChange(realIndex, 'name', e.target.value)}
                                                        style={{ width: 180 }} />
                                                </TableCell>
                                                <TableCell>
                                                    <Select value={player.pos} size="small"
                                                        onChange={(e) => handleFieldChange(realIndex, 'pos', e.target.value)}>
                                                        {POSITIONS.map(p => (
                                                            <MenuItem key={p} value={p}>{p}</MenuItem>
                                                        ))}
                                                    </Select>
                                                </TableCell>
                                                <TableCell>
                                                    <Typography variant="body2">{player.team}</Typography>
                                                </TableCell>
                                                <TableCell>
                                                    <Typography variant="body2">{player.overseas ? 'Yes' : 'No'}</Typography>
                                                </TableCell>
                                                <TableCell>
                                                    <IconButton size="small" onClick={() => handleDelete(realIndex)}>
                                                        <DeleteIcon fontSize="small" />
                                                    </IconButton>
                                                </TableCell>
                                            </TableRow>
                                        );
                                    })}
                                </TableBody>
                            </Table>
                        </div>
                        <Button size="small" startIcon={<AddIcon />} onClick={handleAddPlayer}
                            style={{ marginTop: 8 }}>
                            Add Player
                        </Button>
                    </CardContent>
                </Card>
            )}

            {players.length > 0 && (
                <Button variant="contained" color="primary" onClick={handleSave} disabled={saving}>
                    {saving ? <CircularProgress size={20} /> : 'Save Players'}
                </Button>
            )}

            <Snackbar open={snackbar.open} autoHideDuration={4000}
                onClose={() => setSnackbar({ ...snackbar, open: false })}>
                <Alert severity={snackbar.severity}>{snackbar.message}</Alert>
            </Snackbar>
        </div>
    );
}
